/**
 * Admin Analytics page.
 *
 * Pulls aggregated metrics from /api/admin-analytics.php (5-minute server
 * cache; ?refresh=1 forces a rebuild). Charts are pure-SVG so we can animate
 * them on every load + refresh without pulling in a chart library.
 *
 * Re-mounting the chart components on each fetch (via the `nonce` key) is
 * what restarts the CSS enter animations — there's no imperative animation
 * API.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, RefreshCw, Loader2, Clock, CalendarDays, DollarSign } from 'lucide-react';
import { PieChart, PieSlice } from './charts/PieChart';
import { BarChart, BarDatum } from './charts/BarChart';
import './charts/charts.css';

interface ApiCategoryRow {
  key: string;
  label: string;
  count: number;
}

const toPieSlices = (rows: ApiCategoryRow[]): PieSlice[] =>
  rows.map(r => ({ key: r.key, label: r.label, value: r.count }));

const API_BASE =
  window.location.hostname === 'localhost' ? 'http://chriscme/api' : '/api';

interface Totals {
  estimates: number;
  firstAt: string | null;
  lastAt: string | null;
  elapsedDays: number;
  weeksElapsed: number;
  totalValueCAD: number;
}

interface AnalyticsPayload {
  generatedAt: string;
  cacheAgeSeconds: number;
  cacheTtlSeconds: number;
  totals: Totals;
  calculationMode: ApiCategoryRow[];
  designPreset: ApiCategoryRow[];
  spaceType: ApiCategoryRow[];
  hourOfDay: { hour: number; count: number; avgPerDay: number }[];
  dayOfWeek: { dow: number; label: string; count: number; avgPerWeek: number }[];
  costRangeCAD: ApiCategoryRow[];
}

// Brand-leaning palette: dark navy first (matches Trusscore primary), warm
// yellow second (brand accent), then a clean spectrum for the rest. Eight
// entries means a future preset/space-type addition won't run out of colors.
const PALETTE = [
  '#1a1a2e', // dark navy
  '#f5b400', // brand yellow
  '#3b82f6', // blue
  '#10b981', // green
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#ec4899', // pink
];

const formatRelative = (iso: string): string => {
  const t = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hr ago`;
  return `${Math.floor(diffSec / 86400)} days ago`;
};

const formatHour = (h: number): string => {
  if (h === 0) return '12a';
  if (h === 12) return '12p';
  return h < 12 ? `${h}a` : `${h - 12}p`;
};

export const AdminAnalytics: React.FC = () => {
  const [data, setData] = useState<AnalyticsPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped on every successful load so chart components remount and replay
  // their enter animations.
  const [animationNonce, setAnimationNonce] = useState(0);

  const load = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      const url = `${API_BASE}/admin-analytics.php${forceRefresh ? '?refresh=1' : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      const json = (await res.json()) as AnalyticsPayload;
      setData(json);
      setAnimationNonce(n => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  if (isLoading && !data) {
    return (
      <div className="bg-white rounded-3 shadow-sm p-5 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: 400 }}>
        <Loader2 size={28} style={{ animation: 'spin 1s linear infinite' }} className="text-muted mb-3" />
        <div className="text-muted small">Loading analytics…</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="bg-white rounded-3 shadow-sm p-4">
        <div className="alert alert-warning mb-3"><strong>Couldn't load analytics.</strong> {error}</div>
        <button className="btn btn-primary btn-sm" onClick={() => load(true)}>Try again</button>
      </div>
    );
  }

  if (!data) return null;

  const hourBars: BarDatum[] = data.hourOfDay.map(h => ({
    key: String(h.hour),
    label: formatHour(h.hour),
    value: h.avgPerDay,
  }));
  const dowBars: BarDatum[] = data.dayOfWeek.map(d => ({
    key: String(d.dow),
    label: d.label,
    value: d.avgPerWeek,
  }));

  const calcModeSlices = toPieSlices(data.calculationMode);
  const presetSlices = toPieSlices(data.designPreset);
  const spaceTypeSlices = toPieSlices(data.spaceType);
  const costRangeSlices = toPieSlices(data.costRangeCAD ?? []);

  const totalCalcMode = calcModeSlices.reduce((s, d) => s + d.value, 0);
  const totalSpaceType = spaceTypeSlices.reduce((s, d) => s + d.value, 0);
  const totalPresets = presetSlices.reduce((s, d) => s + d.value, 0);
  const totalCostRange = costRangeSlices.reduce((s, d) => s + d.value, 0);

  return (
    <div className="bg-white rounded-3 shadow-sm p-4">
      {/* Header */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
        <div>
          <h2 className="fw-bold mb-1 d-flex align-items-center gap-2">
            <BarChart3 size={22} /> Analytics
          </h2>
          <p className="text-muted mb-0 small">
            Aggregated breakdown of every completed estimate.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small d-flex align-items-center gap-1">
            <Clock size={13} />
            Updated {formatRelative(data.generatedAt)}
            {data.cacheAgeSeconds > 0 && (
              <span className="text-muted-emphasis"> (cache {Math.round(data.cacheAgeSeconds)}s)</span>
            )}
          </span>
          <button
            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
            onClick={() => load(true)}
            disabled={isRefreshing}
            title="Force a fresh aggregate. Bypasses the 5-minute server cache."
          >
            <RefreshCw size={13} style={isRefreshing ? { animation: 'spin 1s linear infinite' } : undefined} />
            Refresh
          </button>
        </div>
      </div>

      {/* Headline stats */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="border rounded-3 p-3 h-100 d-flex flex-column">
            <div className="text-muted small d-flex align-items-center gap-1">
              <BarChart3 size={13} /> Total estimates
            </div>
            <div className="fs-2 fw-bold">{data.totals.estimates.toLocaleString()}</div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="border rounded-3 p-3 h-100 d-flex flex-column">
            <div className="text-muted small d-flex align-items-center gap-1">
              <DollarSign size={13} /> Total estimated value (CAD)
            </div>
            <div className="fs-2 fw-bold">
              {(data.totals.totalValueCAD ?? 0).toLocaleString('en-CA', {
                style: 'currency',
                currency: 'CAD',
                maximumFractionDigits: 0,
              })}
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="border rounded-3 p-3 h-100 d-flex flex-column">
            <div className="text-muted small d-flex align-items-center gap-1">
              <CalendarDays size={13} /> Window
            </div>
            <div className="fs-5 fw-semibold">{data.totals.elapsedDays.toLocaleString()} days</div>
            <div className="text-muted small">{data.totals.weeksElapsed.toLocaleString()} weeks</div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3">
          <div className="border rounded-3 p-3 h-100 d-flex flex-column">
            <div className="text-muted small d-flex align-items-center gap-1">
              <Clock size={13} /> First → last estimate
            </div>
            <div className="small">
              {data.totals.firstAt ? new Date(data.totals.firstAt).toLocaleDateString() : '—'}
              {' → '}
              {data.totals.lastAt ? new Date(data.totals.lastAt).toLocaleDateString() : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Pie row — 4 across on xl, 2x2 on md, stacked on sm */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-6 col-xl-3">
          <div className="analytics-chart-card">
            <h5>Estimated cost (CAD)</h5>
            <div className="chart-subtitle">Total CAD cost grouped into $2,000 bands.</div>
            <PieChart
              key={`cost-${animationNonce}`}
              data={costRangeSlices}
              palette={PALETTE}
              centerLabel={totalCostRange.toLocaleString()}
              centerSubLabel="estimates"
            />
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="analytics-chart-card">
            <h5>Calculation mode</h5>
            <div className="chart-subtitle">Which path users take through the estimator.</div>
            <PieChart
              key={`calc-${animationNonce}`}
              data={calcModeSlices}
              palette={PALETTE}
              centerLabel={totalCalcMode.toLocaleString()}
              centerSubLabel="estimates"
            />
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="analytics-chart-card">
            <h5>Design preset</h5>
            <div className="chart-subtitle">Among Full Room estimates only.</div>
            <PieChart
              key={`preset-${animationNonce}`}
              data={presetSlices}
              palette={PALETTE}
              centerLabel={totalPresets.toLocaleString()}
              centerSubLabel="full-room"
            />
          </div>
        </div>
        <div className="col-12 col-md-6 col-xl-3">
          <div className="analytics-chart-card">
            <h5>Space type</h5>
            <div className="chart-subtitle">Where the estimator is being applied.</div>
            <PieChart
              key={`space-${animationNonce}`}
              data={spaceTypeSlices}
              palette={PALETTE}
              centerLabel={totalSpaceType.toLocaleString()}
              centerSubLabel="estimates"
            />
          </div>
        </div>
      </div>

      {/* Bar row */}
      <div className="row g-3">
        <div className="col-12 col-xl-7">
          <div className="analytics-chart-card">
            <h5>Hour of day</h5>
            <div className="chart-subtitle">
              Average estimates per hour-of-day across the {data.totals.elapsedDays.toLocaleString()}-day window. America/Toronto.
            </div>
            <BarChart
              key={`hour-${animationNonce}`}
              data={hourBars}
              color={PALETTE[2]}
              peakColor={PALETTE[0]}
              formatValue={n => (n >= 1 ? n.toFixed(1) : n.toFixed(2))}
            />
          </div>
        </div>
        <div className="col-12 col-xl-5">
          <div className="analytics-chart-card">
            <h5>Day of week</h5>
            <div className="chart-subtitle">
              Average estimates per weekday across {data.totals.weeksElapsed.toLocaleString()} weeks.
            </div>
            <BarChart
              key={`dow-${animationNonce}`}
              data={dowBars}
              color={PALETTE[3]}
              peakColor={PALETTE[0]}
              formatValue={n => (n >= 1 ? n.toFixed(1) : n.toFixed(2))}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
